import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Button, Paper, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Grid, Select, MenuItem, FormControl, InputLabel, Alert,
  Autocomplete, useTheme, alpha,
} from '@mui/material';
import {
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Today as TodayIcon,
} from '@mui/icons-material';
import { fetchCalendar, createBooking, updateBooking, deleteBooking } from '../store/slices/bookingsSlice';
import PageHeader from '../components/PageHeader';
import api from '../services/api';
import { fetchVenues } from '../store/slices/venuesSlice';
import { fetchDCPJobs } from '../store/slices/dcpJobsSlice';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function BookingCalendar() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { calendar } = useSelector((state) => state.bookings);
  const { venues } = useSelector((state) => state.venues);
  const { jobs: dcpJobs } = useSelector((state) => state.dcpJobs);

  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth());
  const [addDialog, setAddDialog] = useState(false);
  // Setter is called when the Add dialog opens (records which day) but
  // the value isn't rendered yet — pre-wiring for a future "pre-fill
  // start_date from clicked day" feature. Underscore-prefix opts out
  // of the unused-var lint.
  // eslint-disable-next-line no-unused-vars
  const [_selectedDay, setSelectedDay] = useState(null);
  // When set, the dialog acts as Edit (Save + Delete) instead of Add.
  const [editingBookingId, setEditingBookingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    title: '', venue_id: '', venue_name: '', screen_id: '', screen_name: '', dcp_job_id: '',
    version: '5.1', first_screening: '', last_screening: '',
    test_date: '', delivery_method: 'download', notes: '',
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    const start = new Date(year, month, 1).toISOString();
    const end = new Date(year, month + 1, 0).toISOString();
    dispatch(fetchCalendar({ start, end }));
    dispatch(fetchVenues());
    dispatch(fetchDCPJobs({}));
  }, [dispatch, year, month]);

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };
  const goToday = () => { setYear(new Date().getFullYear()); setMonth(new Date().getMonth()); };

  // Build calendar grid
  const calendarDays = useMemo(() => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = [];

    // Padding for first week
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);
    // Pad to complete last week
    while (days.length % 7 !== 0) days.push(null);

    return days;
  }, [year, month]);

  // Map events to days
  const eventsByDay = useMemo(() => {
    const map = {};
    (calendar || []).forEach(event => {
      const start = new Date(event.start);
      const end = new Date(event.end);
      // Mark every day this event spans
      const d = new Date(start);
      while (d <= end) {
        if (d.getMonth() === month && d.getFullYear() === year) {
          const key = d.getDate();
          if (!map[key]) map[key] = [];
          map[key].push(event);
        }
        d.setDate(d.getDate() + 1);
      }
    });
    return map;
  }, [calendar, month, year]);

  const today = new Date();
  const isToday = (day) => day === today.getDate() && month === today.getMonth() && year === today.getFullYear();

  const handleAddBooking = async () => {
    if (!bookingForm.title || !bookingForm.first_screening || !bookingForm.last_screening) {
      setError('Title and screening dates are required');
      return;
    }
    if (!bookingForm.venue_id && !bookingForm.venue_name) {
      setError('Select or enter a venue');
      return;
    }

    try {
      let venueId = bookingForm.venue_id ? parseInt(bookingForm.venue_id) : null;

      // If venue was typed manually, create it first
      if (!venueId && bookingForm.venue_name) {
        const { data: newVenue } = await api.post('/api/venues/', { name: bookingForm.venue_name });
        venueId = newVenue.id;
        dispatch(fetchVenues()); // refresh venue list
      }

      // If screen was typed manually, create it
      let screenId = bookingForm.screen_id ? parseInt(bookingForm.screen_id) : null;
      if (!screenId && bookingForm.screen_name && venueId) {
        const { data: newScreen } = await api.post(`/api/venues/${venueId}/screens`, { name: bookingForm.screen_name });
        screenId = newScreen.id;
      }

      await dispatch(createBooking({
        title: bookingForm.title,
        venue_id: venueId,
        screen_id: screenId,
        dcp_job_id: bookingForm.dcp_job_id ? parseInt(bookingForm.dcp_job_id) : null,
        version: bookingForm.version,
        delivery_method: bookingForm.delivery_method,
        notes: bookingForm.notes,
        first_screening: new Date(bookingForm.first_screening).toISOString(),
        last_screening: new Date(bookingForm.last_screening).toISOString(),
        test_date: bookingForm.test_date ? new Date(bookingForm.test_date).toISOString() : null,
      })).unwrap();
      setAddDialog(false);
      setError(null);
      // Refresh
      const start = new Date(year, month, 1).toISOString();
      const end = new Date(year, month + 1, 0).toISOString();
      dispatch(fetchCalendar({ start, end }));
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Failed to create booking');
    }
  };

  const openAddForDay = (day) => {
    // Default a single-day screening to 7:00 PM start, 11:59 PM end —
    // standard "evening showtime" anchor. KDM window auto-derives to
    // -14 days / +48 hours from these. Operator types explicit times
    // when the screening is a matinee or multi-day run.
    const datePart = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const startStr = `${datePart}T19:00`;
    const endStr = `${datePart}T23:59`;
    setBookingForm(f => ({
      ...f, first_screening: startStr, last_screening: endStr,
      title: '', venue_id: '', venue_name: '', screen_id: '', screen_name: '', notes: '',
      dcp_job_id: '', version: '5.1', delivery_method: 'download', test_date: '',
    }));
    setSelectedDay(day);
    setEditingBookingId(null);
    setAddDialog(true);
    setError(null);
  };

  const openEditEvent = (event) => {
    // Convert ISO timestamps back to the local datetime-local format
    // YYYY-MM-DDTHH:MM that the input expects.
    const toLocalInput = (iso) => {
      if (!iso) return '';
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    const venue = (venues || []).find(v => v.id === event.venue_id);
    const screen = venue?.screens?.find(s => s.id === event.screen_id);
    setBookingForm({
      title: event.title || '',
      venue_id: event.venue_id ? String(event.venue_id) : '',
      venue_name: venue ? `${venue.chain ? venue.chain + ' ' : ''}${venue.name}` : (event.venue || ''),
      screen_id: event.screen_id ? String(event.screen_id) : '',
      screen_name: screen?.name || event.screen || '',
      dcp_job_id: event.dcp_job_id ? String(event.dcp_job_id) : '',
      version: event.version || '5.1',
      first_screening: toLocalInput(event.start),
      last_screening: toLocalInput(event.end),
      test_date: toLocalInput(event.test_date),
      delivery_method: event.delivery_method || 'download',
      notes: event.notes || '',
    });
    setEditingBookingId(event.id);
    setAddDialog(true);
    setError(null);
  };

  const handleSaveEdit = async () => {
    if (!editingBookingId) return;
    if (!bookingForm.title || !bookingForm.first_screening || !bookingForm.last_screening) {
      setError('Title and screening dates are required');
      return;
    }
    setBusy(true);
    try {
      let venueId = bookingForm.venue_id ? parseInt(bookingForm.venue_id) : null;
      if (!venueId && bookingForm.venue_name) {
        const { data: newVenue } = await api.post('/api/venues/', { name: bookingForm.venue_name });
        venueId = newVenue.id;
        dispatch(fetchVenues());
      }
      let screenId = bookingForm.screen_id ? parseInt(bookingForm.screen_id) : null;
      if (!screenId && bookingForm.screen_name && venueId) {
        const { data: newScreen } = await api.post(`/api/venues/${venueId}/screens`, { name: bookingForm.screen_name });
        screenId = newScreen.id;
      }
      await dispatch(updateBooking({
        bookingId: editingBookingId,
        bookingData: {
          title: bookingForm.title,
          venue_id: venueId,
          screen_id: screenId,
          dcp_job_id: bookingForm.dcp_job_id ? parseInt(bookingForm.dcp_job_id) : null,
          version: bookingForm.version,
          delivery_method: bookingForm.delivery_method,
          notes: bookingForm.notes,
          first_screening: new Date(bookingForm.first_screening).toISOString(),
          last_screening: new Date(bookingForm.last_screening).toISOString(),
          test_date: bookingForm.test_date ? new Date(bookingForm.test_date).toISOString() : null,
        },
      })).unwrap();
      setAddDialog(false);
      setEditingBookingId(null);
      setError(null);
      const startIso = new Date(year, month, 1).toISOString();
      const endIso = new Date(year, month + 1, 0).toISOString();
      dispatch(fetchCalendar({ start: startIso, end: endIso }));
    } catch (err) {
      setError(typeof err === 'string' ? err : (err?.message || 'Failed to save booking'));
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteEdit = async () => {
    if (!editingBookingId) return;
    if (!window.confirm('Cancel this booking? It will be removed from the calendar but kept in history.')) return;
    setBusy(true);
    try {
      await dispatch(deleteBooking(editingBookingId)).unwrap();
      setAddDialog(false);
      setEditingBookingId(null);
      const startIso = new Date(year, month, 1).toISOString();
      const endIso = new Date(year, month + 1, 0).toISOString();
      dispatch(fetchCalendar({ start: startIso, end: endIso }));
    } catch (err) {
      setError(typeof err === 'string' ? err : (err?.message || 'Failed to cancel booking'));
    } finally {
      setBusy(false);
    }
  };

  const selectedVenue = venues.find(v => v.id === parseInt(bookingForm.venue_id));

  const statusLabels = { planned: 'Planned', confirmed: 'Confirmed', delivered: 'Delivered', live: 'Live', completed: 'Done' };

  return (
    <Box>
      <PageHeader
        title="Calendar"
        subtitle="Screening bookings and venue schedule"
        actions={
          <>
            <Tooltip title="Refresh"><IconButton onClick={() => {
              const s = new Date(year, month, 1).toISOString();
              const e = new Date(year, month + 1, 0).toISOString();
              dispatch(fetchCalendar({ start: s, end: e }));
            }}><RefreshIcon /></IconButton></Tooltip>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setAddDialog(true); setError(null); }}>New Booking</Button>
          </>
        }
      />

      {/* Empty-state banner — shows once when there are zero bookings, so
          the calendar doesn't read as "broken / nothing populates here" to
          new users. */}
      {(!calendar || calendar.length === 0) && (
        <Alert severity="info" sx={{ mb: 2 }} icon={<TodayIcon />}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            No bookings yet
          </Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            Bookings link a screening date + venue to a completed DCP, and drive the default KDM validity window when you issue keys. Click <strong>New Booking</strong> above to schedule one — you can attach it to an existing DCP or leave it open for now.
          </Typography>
        </Alert>
      )}

      {/* Month navigation */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <IconButton onClick={prevMonth}><PrevIcon /></IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" fontWeight={600}>{MONTHS[month]} {year}</Typography>
            <Tooltip title="Go to today"><IconButton size="small" onClick={goToday}><TodayIcon fontSize="small" /></IconButton></Tooltip>
          </Box>
          <IconButton onClick={nextMonth}><NextIcon /></IconButton>
        </Box>
      </Paper>

      {/* Calendar grid */}
      <Paper sx={{ overflow: 'hidden' }}>
        {/* Day headers */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: `1px solid ${theme.palette.divider}` }}>
          {DAYS.map(d => (
            <Box key={d} sx={{ p: 1, textAlign: 'center' }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary">{d}</Typography>
            </Box>
          ))}
        </Box>

        {/* Day cells */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
          {calendarDays.map((day, i) => {
            const events = day ? (eventsByDay[day] || []) : [];
            const dayIsToday = day && isToday(day);

            return (
              <Box
                key={i}
                onClick={() => day && openAddForDay(day)}
                sx={{
                  minHeight: 100,
                  p: 0.5,
                  borderRight: (i + 1) % 7 !== 0 ? `1px solid ${alpha(theme.palette.divider, 0.5)}` : 'none',
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                  bgcolor: day ? (dayIsToday ? alpha(theme.palette.primary.main, 0.04) : 'transparent') : alpha(theme.palette.background.default, 0.3),
                  cursor: day ? 'pointer' : 'default',
                  '&:hover': day ? { bgcolor: alpha(theme.palette.primary.main, 0.06) } : {},
                  transition: 'background 0.15s',
                }}
              >
                {day && (
                  <>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'inline-block',
                        width: 24, height: 24, lineHeight: '24px', textAlign: 'center',
                        borderRadius: '50%',
                        fontWeight: dayIsToday ? 700 : 400,
                        bgcolor: dayIsToday ? theme.palette.primary.main : 'transparent',
                        color: dayIsToday ? (isDark ? '#0a0c10' : '#fff') : 'text.secondary',
                        fontSize: '0.75rem',
                      }}
                    >
                      {day}
                    </Typography>
                    <Box sx={{ mt: 0.25 }}>
                      {events.slice(0, 3).map((event, j) => (
                        <Box
                          key={`${event.id}-${j}`}
                          sx={{
                            px: 0.75, py: 0.25, mb: 0.25,
                            borderRadius: 0.5,
                            bgcolor: alpha(event.color, isDark ? 0.2 : 0.12),
                            borderLeft: `3px solid ${event.color}`,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: alpha(event.color, isDark ? 0.32 : 0.22) },
                          }}
                          onClick={(e) => { e.stopPropagation(); openEditEvent(event); }}
                          title="Click to edit"
                        >
                          <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 500, display: 'block', lineHeight: 1.3 }} noWrap>
                            {event.title}
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', lineHeight: 1.2 }} noWrap>
                            {event.venue}{event.screen ? ` — ${event.screen}` : ''}
                          </Typography>
                        </Box>
                      ))}
                      {events.length > 3 && (
                        <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', pl: 0.5 }}>
                          +{events.length - 3} more
                        </Typography>
                      )}
                    </Box>
                  </>
                )}
              </Box>
            );
          })}
        </Box>
      </Paper>

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
        {Object.entries(statusLabels).map(([status, label]) => {
          const colors = { planned: '#607d8b', confirmed: '#039be5', delivered: '#d4a017', live: '#43a047', completed: '#388e3c' };
          return (
            <Box key={status} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: colors[status] }} />
              <Typography variant="caption" color="text.secondary">{label}</Typography>
            </Box>
          );
        })}
      </Box>

      {/* Add / Edit booking dialog */}
      <Dialog open={addDialog} onClose={() => { if (!busy) { setAddDialog(false); setEditingBookingId(null); } }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingBookingId ? 'Edit Booking' : 'Add Booking'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Title" fullWidth required value={bookingForm.title}
              onChange={(e) => setBookingForm(f => ({ ...f, title: e.target.value }))} />

            <Grid container spacing={2}>
              <Grid size={6}>
                <Autocomplete
                  freeSolo
                  options={venues.map(v => ({ id: v.id, label: `${v.chain ? v.chain + ' ' : ''}${v.name}` }))}
                  getOptionLabel={(o) => typeof o === 'string' ? o : o.label}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={bookingForm.venue_id ? venues.map(v => ({ id: v.id, label: `${v.chain ? v.chain + ' ' : ''}${v.name}` })).find(o => o.id === parseInt(bookingForm.venue_id)) || null : null}
                  inputValue={bookingForm.venue_name}
                  onInputChange={(e, val) => setBookingForm(f => ({ ...f, venue_name: val }))}
                  onChange={(e, val) => {
                    if (val && typeof val === 'object') {
                      setBookingForm(f => ({ ...f, venue_id: val.id, venue_name: val.label, screen_id: '', screen_name: '' }));
                    } else {
                      setBookingForm(f => ({ ...f, venue_id: '', screen_id: '', screen_name: '' }));
                    }
                  }}
                  renderInput={(params) => <TextField {...params} label="Venue" placeholder="Select or type new venue" />}
                />
              </Grid>
              <Grid size={6}>
                <Autocomplete
                  freeSolo
                  disabled={!bookingForm.venue_id && !bookingForm.venue_name}
                  noOptionsText={!bookingForm.venue_id ? 'Select a venue first' : 'No screens found'}
                  options={(selectedVenue?.screens || []).filter(s => s.is_active).map(s => ({ id: s.id, label: s.name }))}
                  getOptionLabel={(o) => typeof o === 'string' ? o : o.label}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  inputValue={bookingForm.screen_name}
                  onInputChange={(e, val) => setBookingForm(f => ({ ...f, screen_name: val }))}
                  onChange={(e, val) => {
                    if (val && typeof val === 'object') {
                      setBookingForm(f => ({ ...f, screen_id: val.id, screen_name: val.label }));
                    } else {
                      setBookingForm(f => ({ ...f, screen_id: '' }));
                    }
                  }}
                  renderInput={(params) => <TextField {...params} label="Screen" placeholder={!bookingForm.venue_id && !bookingForm.venue_name ? 'Select a venue first' : 'Select or type new screen'} />}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid size={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>DCP</InputLabel>
                  <Select value={bookingForm.dcp_job_id} label="DCP"
                    onChange={(e) => setBookingForm(f => ({ ...f, dcp_job_id: e.target.value }))}>
                    <MenuItem value="">None selected</MenuItem>
                    {dcpJobs.filter(j => j.status === 'completed').map(j => (
                      <MenuItem key={j.id} value={j.id}>{j.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Version</InputLabel>
                  <Select value={bookingForm.version} label="Version"
                    onChange={(e) => setBookingForm(f => ({ ...f, version: e.target.value }))}>
                    <MenuItem value="5.1">5.1</MenuItem>
                    <MenuItem value="stereo">Stereo</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Delivery</InputLabel>
                  <Select value={bookingForm.delivery_method} label="Delivery"
                    onChange={(e) => setBookingForm(f => ({ ...f, delivery_method: e.target.value }))}>
                    <MenuItem value="download">Download</MenuItem>
                    <MenuItem value="CRU">CRU/Drive</MenuItem>
                    <MenuItem value="network">Network</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <TextField label="Test / Ingest Date" type="datetime-local" fullWidth
              value={bookingForm.test_date} onChange={(e) => setBookingForm(f => ({ ...f, test_date: e.target.value }))}
              InputLabelProps={{ shrink: true }} />
            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField label="First Screening" type="datetime-local" fullWidth required
                  value={bookingForm.first_screening} onChange={(e) => setBookingForm(f => ({ ...f, first_screening: e.target.value }))}
                  InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid size={6}>
                <TextField label="Last Screening" type="datetime-local" fullWidth required
                  value={bookingForm.last_screening} onChange={(e) => setBookingForm(f => ({ ...f, last_screening: e.target.value }))}
                  InputLabelProps={{ shrink: true }} />
              </Grid>
            </Grid>

            <TextField label="Notes" fullWidth multiline rows={2} value={bookingForm.notes}
              onChange={(e) => setBookingForm(f => ({ ...f, notes: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          {editingBookingId && (
            <Button color="error" onClick={handleDeleteEdit} disabled={busy} sx={{ mr: 'auto' }}>
              Cancel Booking
            </Button>
          )}
          <Button onClick={() => { setAddDialog(false); setEditingBookingId(null); }} disabled={busy}>Close</Button>
          <Button
            variant="contained"
            onClick={editingBookingId ? handleSaveEdit : handleAddBooking}
            disabled={busy}
          >
            {editingBookingId ? 'Save Changes' : 'Add Booking'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default BookingCalendar;
